import { type Registry as Services, service } from '@ember/service';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import type {
  BenchmarkInsightCompetitorsCompset,
  BeniSmartCompset,
} from 'lumen/models';
import HotelSettingsCompetitorsRoute from 'lumen/routes/hotel/settings/competitors';
import type { Transition } from 'lumen/services/transition';
import {
  BENI_COMPETITORS_TABS,
  type CompetitorsBenchmarkTab,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

type CompetitorsBenchmarkParams = {
  activeTab: CompetitorsBenchmarkTab;
};

export type BenchmarkInsightSettingsModel = {
  compset: TaskForAsyncTaskFunction<
    { restartable: boolean },
    (
      activeTab: CompetitorsBenchmarkTab,
    ) => Promise<BenchmarkInsightCompetitorsCompset | null>
  >;
  smartCompset: TaskForAsyncTaskFunction<
    { restartable: boolean },
    () => Promise<BeniSmartCompset | null>
  >;
};

export default class HotelSettingsCompetitorsBenchmarkRoute extends HotelSettingsCompetitorsRoute {
  queryParams = {
    activeTab: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service('benchmark-insight/beni-smart-compset-service')
  declare benchmarkInsightBeniSmartCompsetService: Services['benchmark-insight/beni-smart-compset-service'];
  @service('benchmark-insight/competitors/compset')
  declare benchmarkInsightCompetitorsCompset: Services['benchmark-insight/competitors/compset'];
  @service declare router: Services['router'];
  @service declare transition: Services['transition'];

  beforeModel(transition: Transition): void {
    super.beforeModel(transition);

    if (
      !this.access.hasCompsetBenchmark &&
      !this.access.hasSettingsBenchmarkPortfolioCompsetView &&
      !this.access.hasCompsetBenchmarkSmartCompset
    ) {
      this.router.transitionTo('hotel.settings.index');
    }

    this.transition.validateBenchmarkInsightCompetitorsQueryParams(transition);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: CompetitorsBenchmarkParams) {
    const model: BenchmarkInsightSettingsModel = {
      compset: this.benchmarkInsightCompetitorsCompset.fetchCompsetTask,
      smartCompset:
        this.benchmarkInsightBeniSmartCompsetService.smartCompsetTask,
    };
    const { activeTab } = params;

    if (activeTab === BENI_COMPETITORS_TABS.SMART_COMPSET) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      model.smartCompset.perform();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      model.compset.perform(activeTab);
    }

    return model;
  }
}
